import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import * as React from "react";

/**
 * A skeleton loader for the Fee Calculator page
 * This displays placeholder elements with animations while the real data is loading
 */
export const EstimatorSkeleton: React.FC = () => {
  return (
    <Stack direction="column" spacing={2} p={4}>
      {/* Page title */}
      <Skeleton variant="text" width={200} height={40} />

      {/* Main content area */}
      <Stack
        direction="row"
        justifyContent="space-around"
        alignItems="flex-start"
        maxWidth="100%"
      >
        {/* Left column - Inputs */}
        <Stack direction="column" spacing={1} p={2}>
          <Skeleton variant="text" width={100} height={32} />
          <Divider />
          <Box width={600}>
            <Stack direction="column" spacing={3}>
              {/* Aircraft selection */}
              <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="circular" width={20} height={20} />
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Skeleton
                    variant="rectangular"
                    width={375}
                    height={56}
                    sx={{
                      borderRadius: 1,
                      animation: "pulse 1.5s ease-in-out 0.5s infinite",
                    }}
                  />
                  <Stack direction="column" alignItems="center">
                    <Skeleton variant="text" width={100} height={24} />
                    <Skeleton variant="text" width={80} height={24} />
                  </Stack>
                </Stack>
              </Stack>

              {/* Buffer input */}
              <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="text" width={80} height={24} />
                  <Skeleton variant="circular" width={20} height={20} />
                </Stack>
                <Skeleton
                  variant="rectangular"
                  width={125}
                  height={56}
                  sx={{
                    borderRadius: 1,
                    animation: "pulse 1.5s ease-in-out 0.6s infinite",
                  }}
                />
              </Stack>

              {/* Rate inputs */}
              <Stack direction="column" spacing={2}>
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  {/* Base Tenant Rate */}
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="text" width={140} height={24} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </Stack>
                    <Skeleton
                      variant="rectangular"
                      width={200}
                      height={56}
                      sx={{
                        borderRadius: 1,
                        animation: "pulse 1.5s ease-in-out 0.7s infinite",
                      }}
                    />
                  </Stack>

                  {/* Transient Rate */}
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="text" width={120} height={24} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </Stack>
                    <Skeleton
                      variant="rectangular"
                      width={200}
                      height={56}
                      sx={{
                        borderRadius: 1,
                        animation: "pulse 1.5s ease-in-out 0.8s infinite",
                      }}
                    />
                  </Stack>

                  {/* Presets */}
                  <Stack direction="column" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Skeleton variant="text" width={80} height={24} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </Stack>
                    <Skeleton
                      variant="rectangular"
                      width={225}
                      height={56}
                      sx={{
                        borderRadius: 1,
                        animation: "pulse 1.5s ease-in-out 0.9s infinite",
                      }}
                    />
                  </Stack>
                </Stack>

                {/* CAM Fees */}
                <Stack direction="column" spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="text" width={220} height={24} />
                    <Skeleton variant="circular" width={20} height={20} />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton
                      variant="rectangular"
                      width={120}
                      height={56}
                      sx={{
                        borderRadius: 1,
                        animation: "pulse 1.5s ease-in-out 1.0s infinite",
                      }}
                    />
                    <Skeleton variant="text" width={30} height={24} />
                    <Skeleton
                      variant="rectangular"
                      width={140}
                      height={56}
                      sx={{
                        borderRadius: 1,
                        animation: "pulse 1.5s ease-in-out 1.1s infinite",
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        {/* Divider */}
        <Divider orientation="vertical" flexItem />

        {/* Right column - Calculations */}
        <Box width={600}>
          <Stack direction="column" spacing={1} p={2}>
            <Skeleton variant="text" width={120} height={32} />
            <Divider />

            {/* Tabs */}
            <Stack direction="row" spacing={2}>
              <Skeleton
                variant="rectangular"
                width={180}
                height={48}
                sx={{
                  borderRadius: 1,
                  animation: "pulse 1.5s ease-in-out 0.5s infinite",
                }}
              />
              <Skeleton
                variant="rectangular"
                width={120}
                height={48}
                sx={{
                  borderRadius: 1,
                  animation: "pulse 1.5s ease-in-out 0.6s infinite",
                }}
              />
            </Stack>

            {/* Aircraft Square Feet Table */}
            <Box sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="text" width={150} height={24} />
                        <Skeleton variant="circular" width={20} height={20} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="text" width={150} height={24} />
                        <Skeleton variant="circular" width={20} height={20} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={24} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            {/* Fees Table */}
            <Box sx={{ mt: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Skeleton variant="text" width={40} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton
                        variant="text"
                        width={120}
                        height={24}
                        sx={{
                          animation: "pulse 1.5s ease-in-out 0.7s infinite",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(2)].map((_, index) => (
                    <TableRow key={`fee-row-${index}`}>
                      <TableCell>
                        <Skeleton variant="text" width={120} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} height={24} />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          variant="text"
                          width={100}
                          height={24}
                          sx={{
                            animation: "pulse 1.5s ease-in-out 0.8s infinite",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
